// packages import
import { Autocomplete, Box, ListItem, TextField, createFilterOptions } from '@mui/material'
import { FC, SyntheticEvent, useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
// others
import { Maybe, User, UserRole, useFetchAllUsersLazyQuery, useSearchUserLazyQuery } from '../../../generated'
import { GRAPHQL_QUERY_POLICY, GRAPHQL_QUERY_POLICY_CACHE_FIRST } from '../../constants'
import { AppContext, AuthContext, CaseContext } from '../../context'
import { ActionType } from '../../context/AppContextReducer'
import { SortCaseByUserProps, UserRoleOptionType } from '../../interfaceTypes'
import { assigneeDropdownStyle, flexCenter } from '../../theme/styleConstants'
import { badgeBgColor, titleize } from '../../utils'
import { CaseActionType } from './caseReducer'

export const SortCasesByUser: FC<SortCaseByUserProps> = ({ useRefForSortByUser, inputRef }) => {
  const { dispatch, sortCasesByUser } = useContext(CaseContext)
  const { dispatch: reducerDispatch } = useContext(AppContext)
  const [allUsers, setAllUsers] = useState<Maybe<Maybe<User>[]> | undefined>()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300)
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentUser } = useContext(AuthContext)
  const { id, roles } = currentUser || {}

  const autoCompleteUser = allUsers?.map(user => {
    const { id, firstName, lastName, roles } = user ?? {}
    const { role = null } = roles && roles[0] ? roles[0] : {}
    const label = `${`${titleize(firstName || '')} ${titleize(lastName || '')}` || ''}`
    return { id, label, role }
  })

  autoCompleteUser?.unshift({
    id,
    label: 'My Matters',
    role: roles?.[0]?.role || null
  })

  const [fetchAllUsers, { loading }] = useFetchAllUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,
    onError() {},

    onCompleted(data) {
      if (data) {
        const { fetchAllUsers } = data
        if (fetchAllUsers) {
          const { users, response } = fetchAllUsers
          const { status } = response || {}
          if (status === 200 && users) {
            allUsers ? setAllUsers([...allUsers, ...users]) : setAllUsers(users)
          }
        }
      }
    }
  })

  const [searchUser, { loading: searchLoader }] = useSearchUserLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {},

    onCompleted(data) {
      if (data) {
        const {
          searchUser: { users }
        } = data || {}
        if (users && searchTerm) {
          allUsers ? setAllUsers([...allUsers, ...users]) : setAllUsers(users)
        }
      }
    }
  })

  const handleClearable = () => {
    dispatch({ type: CaseActionType.SET_SORT_CASES_BY_USER, sortCasesByUser: undefined })
    reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    searchParams.delete('userId')
    searchParams.delete('currentPage')
    searchParams.delete('userLabel')
    searchParams.delete('userRole')
    setSearchParams(searchParams)
  }

  useEffect(() => {
    if (!debouncedSearchTerm) {
      fetchAllUsers({
        variables: {
          userInput: {
            paginationOptions: {
              limit: 10,
              page: currentPage
            },
            roles: [UserRole.Attorney, UserRole.Paralegal]
          }
        }
      })
    }

    // eslint-disable-next-line
  }, [currentPage, debouncedSearchTerm])

  useEffect(() => {
    setAllUsers([])
    setCurrentPage(1)

    if (searchTerm) {
      searchUser({
        variables: {
          searchUserInput: {
            roles: [UserRole.Attorney, UserRole.Paralegal],
            searchTerm: debouncedSearchTerm
          }
        }
      })
    }

    // eslint-disable-next-line
  }, [debouncedSearchTerm, searchUser])

  useEffect(() => {
    const id = searchParams.get('userId')
    const label = searchParams.get('userLabel')
    const role = searchParams.get('userRole')
    const item = { id: id ?? '', label: label ?? '', role: role as UserRole }
    dispatch({ type: CaseActionType.SET_SORT_CASES_BY_USER, sortCasesByUser: item })

    // eslint-disable-next-line
  }, [searchParams.get('userId')])

  const handleInputChange = (event: SyntheticEvent, newInputValue: string) => setSearchTerm(newInputValue)

  return (
    <Autocomplete
      fullWidth
      value={sortCasesByUser}
      inputValue={searchTerm}
      onInputChange={handleInputChange}
      ref={useRefForSortByUser}
      options={autoCompleteUser || []}
      onChange={(event, item, reason) => {
        const { id, label, role } = item || {}
        if (id && label && role) {
          dispatch({ type: CaseActionType.SET_SORT_CASES_BY_USER, sortCasesByUser: item! })
          reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
          searchParams.set('userId', id)
          searchParams.set('userLabel', label)
          searchParams.set('userRole', role?.toString())
          setSearchParams(searchParams)
        }

        if (reason === 'clear') {
          handleClearable()
        }
      }}
      loading={loading || searchLoader}
      getOptionLabel={(option: UserRoleOptionType) => {
        const { id, label, role } = option || {}
        const labelWithRole = `${label} (${titleize(role || '')})`
        return id ? labelWithRole : ''
      }}
      filterOptions={createFilterOptions({ ignoreCase: true })}
      isOptionEqualToValue={(option, value) => {
        return option?.id === value?.id
      }}
      ListboxProps={{
        onScroll: event => {
          const listboxNode = event.currentTarget
          if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
            setCurrentPage(currentPage + 1)
          }
        },
        role: 'list-box',
        style: {
          maxHeight: '290px'
        }
      }}
      renderOption={(props, option) => {
        const { id, label, role } = option || {}
        const roleSlice = role?.slice(0, 1)
        const roleColor = role === UserRole.Admin ? role : role?.slice(0, 1)

        return (
          <ListItem {...props} key={id}>
            <Box position="relative">
              {label}
              <Box
                component="span"
                sx={{ ...assigneeDropdownStyle, ...flexCenter, background: badgeBgColor(roleColor ?? '') }}
              >
                {roleSlice}
              </Box>
            </Box>
          </ListItem>
        )
      }}
      renderInput={params => <TextField {...params} label="Sort by staff" size="small" inputRef={inputRef} />}
    />
  )
}
